import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { AssignableUserView } from '../../api/generated/models/AssignableUserView';
import {
  assigneeAvatarColor,
  assigneeDisplayName,
  assigneeRowSublabel,
} from '../../utils/assigneeHelpers';

interface PanelCoords {
  top: number;
  left: number;
  width: number;
  placement: 'above' | 'below';
}

/** Must match `.tasks-assignee-picker-panel--portal` height in CSS. */
const PANEL_HEIGHT_PX = 320;
const PANEL_GAP_PX = 4;
const VIEWPORT_EDGE_PX = 8;

interface AssigneePickerProps {
  members: AssignableUserView[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  ariaLabelledBy?: string;
}

const PANEL_Z_INDEX = 1100;

const AssigneePicker: React.FC<AssigneePickerProps> = ({
  members,
  selectedIds,
  onChange,
  disabled = false,
  ariaLabelledBy,
}) => {
  const { t } = useTranslation();
  const listId = useId();
  const fieldRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [panelCoords, setPanelCoords] = useState<PanelCoords | null>(null);

  const memberById = useMemo(() => {
    const map = new Map<string, AssignableUserView>();
    members.forEach((m) => {
      if (m.id) {
        map.set(m.id, m);
      }
    });
    return map;
  }, [members]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return members;
    }
    return members.filter((m) => (m.name ?? '').toLowerCase().includes(q));
  }, [members, query]);

  const measurePanel = useCallback(() => {
    const el = fieldRef.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - PANEL_GAP_PX - VIEWPORT_EDGE_PX;
    const spaceAbove = rect.top - PANEL_GAP_PX - VIEWPORT_EDGE_PX;
    const openBelow = spaceBelow >= PANEL_HEIGHT_PX || spaceBelow >= spaceAbove;
    const placement: PanelCoords['placement'] = openBelow ? 'below' : 'above';

    let top = openBelow
      ? rect.bottom + PANEL_GAP_PX
      : rect.top - PANEL_HEIGHT_PX - PANEL_GAP_PX;

    const maxTop = window.innerHeight - PANEL_HEIGHT_PX - VIEWPORT_EDGE_PX;
    top = Math.max(VIEWPORT_EDGE_PX, Math.min(top, maxTop));

    setPanelCoords({
      top,
      left: rect.left,
      width: rect.width,
      placement,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPanelCoords(null);
      return;
    }
    measurePanel();
  }, [open, measurePanel]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (fieldRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('resize', measurePanel);
    window.addEventListener('scroll', measurePanel, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('resize', measurePanel);
      window.removeEventListener('scroll', measurePanel, true);
    };
  }, [open, measurePanel]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [open]);

  useEffect(() => {
    if (open) {
      window.setTimeout(() => searchRef.current?.focus(), 0);
      setActiveIndex(0);
    } else {
      setQuery('');
    }
  }, [open]);

  const toggleId = useCallback(
    (userId: string) => {
      if (disabled) {
        return;
      }
      if (selectedIds.includes(userId)) {
        onChange(selectedIds.filter((id) => id !== userId));
      } else {
        onChange([...selectedIds, userId]);
      }
    },
    [disabled, onChange, selectedIds],
  );

  const removeId = useCallback(
    (userId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      if (disabled) {
        return;
      }
      onChange(selectedIds.filter((id) => id !== userId));
    },
    [disabled, onChange, selectedIds],
  );

  const handleFieldKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
    }
    if (event.key === 'Escape' && open) {
      event.stopPropagation();
      setOpen(false);
    }
  };

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      return;
    }
    if (filtered.length === 0) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % filtered.length);
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length);
    }
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      const row = filtered[activeIndex];
      if (row?.id) {
        toggleId(row.id);
      }
    }
  };

  const selectedChips = selectedIds.map((id) => {
    const member = memberById.get(id);
    return {
      id,
      name: member?.name ?? id,
      initials: member?.initials ?? '?',
    };
  });

  const panelContent = open && !disabled && panelCoords && (
    <div
      ref={panelRef}
      className="tasks-assignee-picker-panel tasks-assignee-picker-panel--portal"
      data-placement={panelCoords.placement}
      style={{
        position: 'fixed',
        top: panelCoords.top,
        left: panelCoords.left,
        width: panelCoords.width,
        height: PANEL_HEIGHT_PX,
        zIndex: PANEL_Z_INDEX,
      }}
    >
      <input
        ref={searchRef}
        type="search"
        className="tasks-assignee-picker-search"
        placeholder={t('tasks_assignee_search')}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setActiveIndex(0);
        }}
        onKeyDown={handleListKeyDown}
        aria-label={t('tasks_assignee_search')}
      />
      <p className="tasks-assignee-picker-section-label">{t('tasks_assignee_team_label')}</p>
      <div className="tasks-assignee-picker-list-scroll">
        <ul
          id={listId}
          role="listbox"
          className="tasks-assignee-picker-list"
          onKeyDown={handleListKeyDown}
        >
          {filtered.length === 0 ? (
            <li className="tasks-assignee-picker-empty">{t('tasks_assignee_no_match')}</li>
          ) : (
            filtered.map((member, index) => {
              if (!member.id) {
                return null;
              }
              const checked = selectedIds.includes(member.id);
              const sublabel = assigneeRowSublabel(member, t);
              return (
                <li key={member.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={checked}
                    className={`tasks-assignee-picker-row${index === activeIndex ? ' tasks-assignee-picker-row--active' : ''}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => toggleId(member.id!)}
                  >
                    <input
                      type="checkbox"
                      className="tasks-assignee-picker-checkbox"
                      checked={checked}
                      readOnly
                      tabIndex={-1}
                      aria-hidden="true"
                    />
                    <span
                      className="tasks-assignee-picker-row-avatar"
                      style={{ backgroundColor: assigneeAvatarColor(member.id) }}
                      aria-hidden="true"
                    >
                      {member.initials?.trim() || '?'}
                    </span>
                    <span className="tasks-assignee-picker-row-text">
                      <span className="tasks-assignee-picker-row-name">
                        {assigneeDisplayName(member, t)}
                      </span>
                      {sublabel && (
                        <span className="tasks-assignee-picker-row-sub">{sublabel}</span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
      <div className="tasks-assignee-picker-footer">
        <div className="tasks-assignee-picker-footer-left">
          <span>{t('tasks_assignees_selected', { count: selectedIds.length })}</span>
          <button
            type="button"
            className="tasks-assignee-picker-clear"
            disabled={selectedIds.length === 0}
            onClick={() => onChange([])}
          >
            {t('tasks_assignees_clear_all')}
          </button>
        </div>
        <button
          type="button"
          className="tasks-assignee-picker-done"
          onClick={() => setOpen(false)}
        >
          {t('tasks_assignees_done')}
        </button>
      </div>
    </div>
  );

  return (
    <div className="tasks-assignee-picker">
      <div
        ref={fieldRef}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        aria-labelledby={ariaLabelledBy}
        tabIndex={disabled ? -1 : 0}
        className={`tasks-assignee-picker-field${open ? ' tasks-assignee-picker-field--open' : ''}${disabled ? ' tasks-assignee-picker-field--disabled' : ''}`}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={handleFieldKeyDown}
      >
        <div className="tasks-assignee-picker-chips">
          {selectedChips.length === 0 ? (
            <span className="tasks-assignee-picker-placeholder">{t('tasks_assign_users')}</span>
          ) : (
            selectedChips.map((chip) => (
              <span key={chip.id} className="tasks-assignee-chip">
                <span
                  className="tasks-assignee-chip-avatar"
                  style={{ backgroundColor: assigneeAvatarColor(chip.id) }}
                  aria-hidden="true"
                >
                  {chip.initials}
                </span>
                <span className="tasks-assignee-chip-name">{chip.name}</span>
                {!disabled && (
                  <button
                    type="button"
                    className="tasks-assignee-chip-remove"
                    aria-label={t('tasks_assignee_remove', { name: chip.name })}
                    onClick={(event) => removeId(chip.id, event)}
                  >
                    ×
                  </button>
                )}
              </span>
            ))
          )}
        </div>
        <span className="tasks-assignee-picker-chevron" aria-hidden="true">
          ▾
        </span>
      </div>

      <p className="tasks-assignee-picker-hint">{t('tasks_assignees_hint')}</p>

      {panelContent && createPortal(panelContent, document.body)}
    </div>
  );
};

export default AssigneePicker;
